import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../../data/gallery";

export default function Gallery({ t }) {
  const [index, setIndex] = useState(null);
  const isOpen = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    () => setIndex((i) => (i === null ? i : (i - 1 + GALLERY.length) % GALLERY.length)),
    [],
  );
  const next = useCallback(
    () => setIndex((i) => (i === null ? i : (i + 1) % GALLERY.length)),
    [],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, close, prev, next]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[#F5F1EA] py-24 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-10 bg-[#BEAF87]" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-[#BEAF87] font-[Barlow_Semi_Condensed] font-medium">
                {t.gallery.eyebrow}
              </span>
            </div>
            <h2
              data-testid="gallery-title"
              className="font-[Cormorant_Garamond] font-light text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              {t.gallery.title}
            </h2>
          </div>
          <p className="text-[#3a3a3a] font-[Barlow_Semi_Condensed] max-w-md lg:text-right">
            {t.gallery.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[220px] gap-4">
          {GALLERY.map((img, i) => {
            const spanClass =
              img.span === "tall"
                ? "row-span-2"
                : img.span === "wide"
                ? "col-span-2"
                : "";
            return (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                data-testid={`gallery-item-${i}`}
                aria-label={img.alt}
                className={`group relative overflow-hidden bg-[#0A0A0A]/10 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BEAF87] ${spanClass}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </button>
            );
          })}
        </div>
      </div>

      {isOpen && (
        <div
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={GALLERY[index].alt}
          onClick={close}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A]/95 backdrop-blur-sm animate-fade-up"
        >
          <button
            type="button"
            onClick={close}
            data-testid="gallery-lightbox-close"
            aria-label="Fechar"
            className="absolute top-5 right-5 lg:top-8 lg:right-8 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white hover:border-[#BEAF87] hover:text-[#BEAF87] transition-colors"
          >
            <X size={20} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            data-testid="gallery-lightbox-prev"
            aria-label="Anterior"
            className="absolute left-3 lg:left-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/30 text-white hover:border-[#BEAF87] hover:text-[#BEAF87] transition-colors"
          >
            <ChevronLeft size={24} />
          </button>

          <figure
            onClick={(e) => e.stopPropagation()}
            className="mx-16 lg:mx-24 max-h-[88vh] max-w-[92vw] flex flex-col items-center"
          >
            <img
              src={GALLERY[index].src}
              alt={GALLERY[index].alt}
              className="max-h-[80vh] max-w-full object-contain shadow-2xl"
            />
            <figcaption className="mt-4 text-center text-sm text-white/70 font-[Barlow_Semi_Condensed]">
              {GALLERY[index].alt}
              <span className="ml-3 text-white/40">
                {index + 1} / {GALLERY.length}
              </span>
            </figcaption>
          </figure>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            data-testid="gallery-lightbox-next"
            aria-label="Seguinte"
            className="absolute right-3 lg:right-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/30 text-white hover:border-[#BEAF87] hover:text-[#BEAF87] transition-colors"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </section>
  );
}
